/**
 * Der Support-Zugriff der angemeldeten Person — eine Abfrage für die ganze App.
 *
 * Zwei Stellen brauchen denselben Stand: die Hinweisleiste über jeder Seite
 * („Der Support bittet um Zugriff“) und der Reiter „Support-Zugriff“ in den
 * Einstellungen. Beantwortet die Person die Anfrage dort, muss die Leiste im
 * selben Moment verschwinden — deshalb hält ein Provider den Stand, statt dass
 * jede Stelle selbst fragt (eine Anfrage je Seitenaufruf, nicht zwei).
 *
 * Während einer Impersonation fragt der Provider nicht: Die Leiste gilt dem
 * Nutzer, nicht dem Support in seinem Konto, und ändern ließe sich ohnehin
 * nichts (Schreibsperre im Backend).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { components } from '../types/api.generated';
import { authAPI } from '../services/api';
import { useAuth } from './useAuth';

export type SupportZugriff = components['schemas']['SupportAccessResponse'];
export type SupportDauer = components['schemas']['SupportAccessGrantBody']['duration'];

/** Dieselben Fristen wie `apps121_core.support_zugriff.DAUERN` im Backend. */
export const SUPPORT_DAUERN: readonly SupportDauer[] = ['1h', '24h', '7d'];

interface Wert {
  /** `null`, solange nichts geladen ist (oder niemand angemeldet). */
  stand: SupportZugriff | null;
  laedt: boolean;
  /** Das Fehlerobjekt — der Satz entsteht beim Rendern (`fehlerText`). */
  fehler: unknown;
  neuLaden: () => Promise<void>;
  /** Nach einer Änderung: die Antwort des Backends ist der neue Stand. */
  uebernehmen: (neu: SupportZugriff) => void;
}

const Kontext = createContext<Wert | null>(null);

export function SupportZugriffProvider({ children }: { children: ReactNode }) {
  // Erst nach `/me`: Vorher steht in `user` der Cache, nicht die Sitzung.
  const { sitzungsNutzer, isImpersonating } = useAuth() as {
    sitzungsNutzer: { id: number } | null
    isImpersonating: boolean
  };
  const nutzerId = !sitzungsNutzer || isImpersonating ? null : sitzungsNutzer.id;
  const [stand, setStand] = useState<SupportZugriff | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  // Eine Antwort für ein ANDERES Konto (Abmelden, Mandantenwechsel) setzt nichts.
  const fuerNutzer = useRef<number | null>(nutzerId);
  fuerNutzer.current = nutzerId;

  const neuLaden = useCallback(async () => {
    const id = fuerNutzer.current;
    if (id === null) return;
    setLaedt(true);
    try {
      const { data } = await authAPI.getSupportAccess();
      if (fuerNutzer.current !== id) return;
      setStand(data as SupportZugriff);
      setFehler(null);
    } catch (err) {
      console.error('[Support-Zugriff] Stand nicht abrufbar', err);
      if (fuerNutzer.current === id) setFehler(err);
    } finally {
      if (fuerNutzer.current === id) setLaedt(false);
    }
  }, []);

  useEffect(() => {
    setStand(null);
    setFehler(null);
    if (nutzerId !== null) void neuLaden();
  }, [nutzerId, neuLaden]);

  const wert = useMemo<Wert>(
    () => ({ stand, laedt, fehler, neuLaden, uebernehmen: setStand }),
    [stand, laedt, fehler, neuLaden],
  );
  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useSupportZugriff(): Wert {
  const wert = useContext(Kontext);
  if (!wert) throw new Error('useSupportZugriff braucht den SupportZugriffProvider');
  return wert;
}
